import { getSessionStorage } from 'src/functions/localStorageHandler';
import { getLanguageFromPath } from 'src/utils/i18n-config';
import { InteractionEvent, InteractionType } from './trackInteractionTypes';
import { getPageLoadTimestamp, getSessionPageCount, getUtmParams, getActiveExperiments } from './pageState';

const getViewportType = (width: number): 'desktop' | 'tablet' | 'mobile' => {
  if (width >= 1024) return 'desktop';
  if (width >= 768) return 'tablet';
  return 'mobile';
};

export const getScrollPercent = (): number => {
  if (typeof window === 'undefined') return 0;
  const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollHeight <= 0) return 100;
  return Math.round((window.scrollY / scrollHeight) * 100);
};

const getElementText = (element?: HTMLElement | null): string => {
  if (!element) return '';
  return (element.textContent || '').trim().substring(0, 100);
};

const getElementDestination = (element?: HTMLElement | null): string | null => {
  if (!element) return null;
  const anchor = element.closest('a') as HTMLAnchorElement | null;
  if (anchor?.href) return anchor.href;
  if ((element as HTMLAnchorElement).href) return (element as HTMLAnchorElement).href;
  return element.getAttribute('href') || null;
};

const getElementPositionY = (element?: HTMLElement | null): number | null => {
  if (!element) return null;
  return Math.round(element.getBoundingClientRect().top + window.scrollY);
};

const getInternalReferrer = (): string | null => {
  if (typeof window === 'undefined') return null;
  const state = (window as any).__gatsby?.router?.state?.location?.state;
  return state?.referrer || null;
};

export const buildInteractionEvent = (
  id: string,
  section: string,
  type: InteractionType,
  element?: HTMLElement | null
): InteractionEvent => {
  const utmParams = getUtmParams();
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  const experiments = getActiveExperiments();
  const experimentParams: Record<string, string> = {};
  for (const [name, variant] of Object.entries(experiments)) {
    experimentParams[`exp_${name}`] = variant;
  }

  return {
    event: 'user_interaction',
    interaction_id: id,
    interaction_section: section,
    interaction_type: type,
    interaction_text: getElementText(element),
    interaction_destination: getElementDestination(element),
    element_position_y: getElementPositionY(element),
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    page_referrer: typeof document !== 'undefined' ? document.referrer : '',
    page_internal_referrer: getInternalReferrer(),
    page_language: typeof window !== 'undefined' ? getLanguageFromPath(window.location.pathname) : 'en',
    scroll_depth_percent: getScrollPercent(),
    time_on_page_seconds: Math.round((Date.now() - getPageLoadTimestamp()) / 1000),
    session_page_count: getSessionPageCount(),
    viewport_type: getViewportType(viewportWidth),
    viewport_width: viewportWidth,
    utm_source: utmParams.source,
    utm_medium: utmParams.medium,
    utm_campaign: utmParams.campaign,
    is_first_visit: getSessionStorage('@statsig:firstTouch') === 'true',
    click_timestamp: Date.now(),
    ...experimentParams,
  };
};

const GA4_MP_PROXY_PATH = '/gtg/mp-collect';
const GA4_SESSION_COOKIE_ID = '7M2C06HKGP';

let mpProxyUrl = GA4_MP_PROXY_PATH;
export const __setGA4ConfigForTesting__ = (proxyUrl: string): void => {
  mpProxyUrl = proxyUrl;
};

const getGA4ClientId = (): string => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/_ga=GA1\.\d+\.([^;]+)/);
  if (match) return match[1];
  return (window as any).localStorage?.getItem('statsigUserId') || 'unknown';
};

export const getGA4SessionId = (): string => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`_ga_${GA4_SESSION_COOKIE_ID}=GS\\d+\\.\\d+\\.s(\\d+)`));
  return match ? match[1] : '';
};

export const pushToGA4 = (eventData: InteractionEvent): void => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

  const { event, ...params } = eventData;
  const clientId = getGA4ClientId();
  const sessionId = getGA4SessionId();

  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined)
  );

  const payload = JSON.stringify({
    client_id: clientId,
    events: [{
      name: event,
      params: {
        ...cleanParams,
        ...(sessionId && { session_id: sessionId }),
        engagement_time_msec: Math.max(1, (cleanParams.time_on_page_seconds || 0) * 1000),
      },
    }],
  });

  const url = mpProxyUrl;

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, payload);
  } else {
    fetch(url, { method: 'POST', body: payload, keepalive: true });
  }
};
